/**
 * Registro de plantillas white-label (CONTRATO admin ↔ botón nativo).
 *
 * Este archivo es la fuente de verdad en el lado admin para:
 *  - los ids de plantilla de login y botonera que se guardan en `cliente.loginTemplate` /
 *    `cliente.inicioTemplate` (campos nuevos de ICliente), y
 *  - el tamaño de imagen esperado por cada slot según la plantilla elegida.
 *
 * Debe mantenerse SINCRONIZADO con `seguridad-boton-nativo` (dispatchers + ClienteRuntime) y con
 * `seguridad-boton-docs/docs/02-templates`. Un id desconocido en el nativo cae a un fallback
 * silencioso (`default` / `grid`).
 *
 * MODELO (importante, leer antes de tocar):
 *  - Estas plantillas (`grid|banner|centrado|hub`) son las GENÉRICAS. Se eligen por categoría vía
 *    `categoria.inicioTemplate` (campo opcional). SOLO aplican a clientes genéricos.
 *  - Los templates CUSTOM / hand-made (Quilmes, Hurlingham, Brown, …) NO están acá: son Composables
 *    a mano en el nativo y los resuelve el BUNDLE ID (`Clientes.forBundleId` / seed). El bundleId es
 *    quien identifica al cliente y su template; no se elige de una lista ni hace falta una "marca".
 *  - La variación POR CATEGORÍA dentro de un template (genérico o custom) la da `categoria.
 *    nombreTemplate` (la variante: "normal" / "violencia de genero" / …), que el admin SIEMPRE deja
 *    cargar. Para los custom, esa es la única pieza por categoría — `categoria.inicioTemplate` no
 *    aplica (el bundleId manda).
 *
 * Tamaños: el render nativo (Compose) restringe por ALTO en dp. Para que la imagen quede nítida en
 * pantallas @3x, el px fuente recomendado = dp × 3 (ver `dp2px`). El ancho sigue el aspecto.
 */

/** Densidad objetivo para el px fuente recomendado (la más alta común → nítido en todas). */
export const DENSIDAD = 3;

/** dp de Compose → px fuente recomendado @3x. */
export const dp2px = (dp: number): number => dp * DENSIDAD;

export type LoginTemplateId = 'default' | 'logo-centrado' | 'hero';
export type BotoneraTemplateId = 'grid' | 'banner' | 'centrado' | 'hub';

/** Spec de un slot de imagen: tamaño fuente recomendado + cómo lo escala el nativo. */
export interface SlotSpec {
  /** Clave del slot. Para botones es el control del form (`urlImagen`, etc.). */
  slot: string;
  /** Etiqueta legible para el operador. */
  label: string;
  /** Ancho px fuente recomendado (@3x). */
  w: number;
  /** Alto px fuente recomendado (@3x). */
  h: number;
  /** Aspecto nominal ('1:1', '3:2', 'device', 'libre'). */
  aspecto: string;
  /** ContentScale del nativo: condiciona si conviene recortar o no. */
  scale: 'Fit' | 'FillWidth' | 'Crop';
  /** Alto en dp que aplica el template (referencia). */
  dp?: number;
  nota?: string;
}

export interface LoginSpec {
  id: LoginTemplateId;
  label: string;
  descripcion: string;
  slots: SlotSpec[];
}

export interface BotoneraSpec {
  id: BotoneraTemplateId;
  label: string;
  descripcion: string;
  /** Aliases legacy por nombre de cliente (solo referencia del contrato). */
  aliasLegacy: string[];
  slots: SlotSpec[];
}

// ============================ LOGIN ============================

export const LOGIN_SPECS: Record<LoginTemplateId, LoginSpec> = {
  default: {
    id: 'default',
    label: 'Default (fondo + card)',
    descripcion:
      'Fondo de color de marca + card blanca con teléfono/SMS. Logo arriba. La que ya está en producción.',
    slots: [
      {
        slot: 'login',
        label: 'Logo / header',
        w: dp2px(360),
        h: dp2px(120),
        aspecto: '3:1',
        scale: 'Fit',
        dp: 120,
        nota: 'Cae a icono/lateral si falta.',
      },
    ],
  },
  'logo-centrado': {
    id: 'logo-centrado',
    label: 'Logo centrado / minimal',
    descripcion:
      'Logo grande centrado sobre el color de marca, campo de teléfono único, sin card. Fondo opcional.',
    slots: [
      {
        slot: 'login',
        label: 'Logo centrado',
        w: dp2px(360),
        h: dp2px(160),
        aspecto: '~9:4',
        scale: 'Fit',
        dp: 160,
      },
      {
        slot: 'fondo',
        label: 'Fondo (opcional)',
        w: 1242,
        h: 2688,
        aspecto: 'device',
        scale: 'Crop',
        nota: 'Pantalla completa. Opcional; si falta usa color de marca.',
      },
    ],
  },
  hero: {
    id: 'hero',
    label: 'Hero (banner sobre color)',
    descripcion:
      'Fondo del color del tema + el banner (imagenes.login) centrado arriba + formulario debajo.',
    slots: [
      {
        slot: 'login',
        label: 'Banner (centrado)',
        w: dp2px(360),
        h: dp2px(120),
        aspecto: '~3:1',
        scale: 'Fit',
        dp: 120,
        nota: 'Banner centrado sobre el color del tema (no a todo el ancho).',
      },
    ],
  },
};

// ============================ BOTONERA ============================

export const BOTONERA_SPECS: Record<BotoneraTemplateId, BotoneraSpec> = {
  grid: {
    id: 'grid',
    label: 'Grid 3-páginas (default)',
    descripcion:
      'Grilla de botones-imagen paginada (principales / secundarios / otros). Fallback genérico.',
    aliasLegacy: ['default'],
    slots: [
      {
        slot: 'arriba',
        label: 'Logo superior',
        w: dp2px(360),
        h: dp2px(96),
        aspecto: '~3.75:1',
        scale: 'Fit',
        dp: 96,
      },
      {
        slot: 'urlImagen',
        label: 'Imagen del botón',
        w: dp2px(360),
        h: dp2px(88),
        aspecto: '~2.5:1',
        scale: 'Fit',
        dp: 88,
      },
      {
        slot: 'reflector',
        label: 'Reflector',
        w: dp2px(120),
        h: dp2px(96),
        aspecto: '1:1',
        scale: 'Fit',
        dp: 96,
      },
      {
        slot: 'sirena',
        label: 'Sirena',
        w: dp2px(120),
        h: dp2px(96),
        aspecto: '1:1',
        scale: 'Fit',
        dp: 96,
      },
      {
        slot: 'fondo',
        label: 'Fondo',
        w: 1242,
        h: 2688,
        aspecto: 'device',
        scale: 'Crop',
      },
    ],
  },
  banner: {
    id: 'banner',
    label: 'Banner + fila de acciones',
    descripcion:
      'Banner grande arriba + botones principales + fila Reflector / Chat / Sirena con etiquetas.',
    aliasLegacy: ['lanus', 'lanús'],
    slots: [
      {
        slot: 'arriba',
        label: 'Banner superior',
        w: dp2px(360),
        h: dp2px(220),
        aspecto: '~1.6:1',
        scale: 'FillWidth',
        dp: 220,
      },
      {
        slot: 'urlImagen',
        label: 'Botón principal',
        w: dp2px(360),
        h: dp2px(88),
        aspecto: '~2.5:1',
        scale: 'Fit',
        dp: 88,
      },
      {
        slot: 'reflector',
        label: 'Reflector (fila)',
        w: dp2px(120),
        h: dp2px(84),
        aspecto: '1:1',
        scale: 'Fit',
        dp: 84,
      },
      {
        slot: 'sirena',
        label: 'Sirena (fila)',
        w: dp2px(120),
        h: dp2px(84),
        aspecto: '1:1',
        scale: 'Fit',
        dp: 84,
      },
    ],
  },
  centrado: {
    id: 'centrado',
    label: 'Centrado (1 botón grande)',
    descripcion:
      'Pensado para UN botón principal grande y centrado → la imagen va CUADRADA (1:1), no rectángulo. Si hay varios, se apilan más chicos. + fila reflector/sirena.',
    aliasLegacy: ['lujan', 'luján'],
    slots: [
      {
        slot: 'arriba',
        label: 'Banner superior',
        w: dp2px(360),
        h: dp2px(180),
        aspecto: '2:1',
        scale: 'FillWidth',
        dp: 180,
      },
      {
        slot: 'urlImagen',
        label: 'Botón único (1 botón)',
        w: dp2px(360),
        h: dp2px(360),
        aspecto: '1:1',
        scale: 'Fit',
        dp: 200,
        nota: 'Un solo botón → imagen CUADRADA, grande y centrada.',
      },
      {
        slot: 'urlImagen-rect',
        label: 'Botón rectangular (2–3 botones)',
        w: dp2px(360),
        h: dp2px(220),
        aspecto: '~3:2',
        scale: 'Fit',
        dp: 160,
        nota: '2 a 3 botones → rectangulares.',
      },
      {
        slot: 'urlImagen-multi',
        label: 'Botón chico (4+ botones)',
        w: dp2px(360),
        h: dp2px(120),
        aspecto: '~2.5:1',
        scale: 'Fit',
        dp: 120,
        nota: '4 o más → más chicos, lista scrolleable.',
      },
      {
        slot: 'reflector',
        label: 'Reflector',
        w: dp2px(120),
        h: dp2px(80),
        aspecto: '1:1',
        scale: 'Fit',
        dp: 80,
      },
      {
        slot: 'sirena',
        label: 'Sirena',
        w: dp2px(120),
        h: dp2px(80),
        aspecto: '1:1',
        scale: 'Fit',
        dp: 80,
      },
    ],
  },
  hub: {
    id: 'hub',
    label: 'Hub / menú',
    descripcion:
      'Menú de categorías (tiles) que navega a subpáginas de botones. Para clientes con muchas opciones.',
    aliasLegacy: ['ezeiza'],
    slots: [
      {
        slot: 'arriba',
        label: 'Logo superior',
        w: dp2px(360),
        h: dp2px(80),
        aspecto: '~9:4',
        scale: 'Fit',
        dp: 80,
      },
      {
        slot: 'urlImagen',
        label: 'Tile / botón',
        w: dp2px(360),
        h: dp2px(130),
        aspecto: '~1.2:1',
        scale: 'Fit',
        dp: 130,
      },
      {
        slot: 'fondo',
        label: 'Fondo',
        w: 1242,
        h: 2688,
        aspecto: 'device',
        scale: 'Crop',
      },
    ],
  },
};

/** Opciones para los `mat-select` de plantilla (login y botonera). */
export const LOGIN_OPCIONES = Object.values(LOGIN_SPECS).map((s) => ({
  id: s.id,
  label: s.label,
  descripcion: s.descripcion,
}));

export const BOTONERA_OPCIONES = Object.values(BOTONERA_SPECS).map((s) => ({
  id: s.id,
  label: s.label,
  descripcion: s.descripcion,
}));

/**
 * Hint de tamaño de imagen para el slot de un botón, según la botonera elegida.
 * Si la botonera tiene variante "única vs múltiple" (centrado), `cantidadPrincipales` decide cuál.
 */
/**
 * Tamaño del botón principal según CANTIDAD (regla universal, igual en todas las botoneras):
 * 1 → cuadrado (1:1) · 2-3 → rectangular (~3:2) · 4+ → chico (~2.5:1), lista scrolleable.
 */
export const BOTON_TIERS: Record<'uno' | 'pocos' | 'muchos', SlotSpec> = {
  uno: {
    slot: 'urlImagen',
    label: 'Botón único (1)',
    w: dp2px(360),
    h: dp2px(360),
    aspecto: '1:1',
    scale: 'Fit',
    nota: '1 botón → imagen cuadrada, grande y centrada.',
  },
  pocos: {
    slot: 'urlImagen',
    label: 'Botón rectangular (2–3)',
    w: dp2px(360),
    h: dp2px(240),
    aspecto: '~3:2',
    scale: 'Fit',
    nota: '2 a 3 botones → rectangulares.',
  },
  muchos: {
    slot: 'urlImagen',
    label: 'Botón chico (4+)',
    w: dp2px(360),
    h: dp2px(144),
    aspecto: '~2.5:1',
    scale: 'Fit',
    nota: '4 o más → más chicos, lista scrolleable.',
  },
};

export function hintTamanoBoton(
  botonera: BotoneraTemplateId | undefined,
  cantidadPrincipales = 1,
): SlotSpec | undefined {
  if (!botonera) return undefined;
  return cantidadPrincipales <= 1
    ? BOTON_TIERS.uno
    : cantidadPrincipales <= 3
      ? BOTON_TIERS.pocos
      : BOTON_TIERS.muchos;
}

/** Texto corto "Subí 1080×440 px (~2.5:1)" para mostrar junto a un upload. */
export function textoHint(spec?: SlotSpec): string {
  if (!spec) return '';
  return `Subí ${spec.w}×${spec.h} px (${spec.aspecto})`;
}
